import { $t } from '@/i18n';
import { cloneDeep, set } from 'lodash';
import { getAvailableComponent } from '../comps-loader';
import { globalConstantService } from '@/workbench/modules/services/global-constant';
import { SSO_DEPLOY } from '@/config/constant';
import getUtilsSchema from './utils-schema';
import getFlowSchema from './flow-schema';
import getOrgSchema from './org-schema';
// 预设字段对应的组件
const COMP = {
    readonlyText: 'w-readonly',
    boolean: 'w-boolean',
    jsonText: 'w-json',
    avatar: 'w-avatar',
    image: 'w-image',
};
const fitSchemaFieldUIType = (schema) => {
    // 已经在 schema 定义 ui 的字段不做处理
    if (schema.ui)
        return schema;
    // 只读字符串
    if (schema.type === 'string' && schema.readonly) {
        set(schema, 'ui.type', getAvailableComponent('public', COMP.readonlyText));
    }
    // 布尔值
    if (schema.type === 'boolean') {
        set(schema, 'ui.type', getAvailableComponent('public', COMP.boolean));
        if (schema.readonly) {
            set(schema, 'ui.props.disabled', true);
        }
    }
    // 只读对象/数组
    if (schema.type && ['object', 'array'].includes(schema.type) && schema.readonly) {
        set(schema, 'ui.type', getAvailableComponent('public', COMP.jsonText));
        set(schema, 'ui.props.readonly', true);
    }
    if (schema.type === 'object' && Array.isArray(schema.fields)) {
        schema.fields.forEach(field => fitSchemaFieldUIType(field));
    }
    return schema;
};
const getNavigateArguments = () => [
    {
        id: 'option',
        type: 'object',
        description: $t('k_6ff01997'),
        fields: [
            {
                id: 'id',
                type: 'string',
            },
            {
                id: 'path',
                type: 'string',
            },
            {
                id: 'query',
                type: 'object',
            },
            {
                id: 'params',
                type: 'object',
            },
        ],
    },
];
const getPageInfoFields = () => [
    { id: 'pageId', title: $t('k_74fc2ea3'), type: 'string', readonly: true },
    { id: 'name', title: $t('k_4df10511'), type: 'string', readonly: true },
    { id: 'title', title: $t('k_218fe524'), type: 'string', readonly: true },
    { id: 'path', title: $t('k_f91f1a04'), type: 'string', readonly: true },
];
export const updateGlobalConstantSchema = async (schema) => {
    if (window.w?.mode !== 'editor')
        return;
    if (!window.w?.projectId)
        return;
    const constList = await globalConstantService.list(window.w.projectId);
    const frontConstantList = constList.filter(item => !item.onlyBackend);
    patchWGlobalSchema(schema, frontConstantList);
};
export const patchWGlobalSchema = (schema, constList) => {
    if (schema.id !== 'w' && schema.id !== 'wuji')
        return;
    const fields = schema.fields || [];
    const globalField = fields.find(item => item.id === 'global');
    if (!globalField) {
        fields.push(getGlobalConstantSchema(constList));
        return;
    }
    globalField.fields = getGlobalConstantFields(constList);
};
export const getGlobalConstantFields = (constList) => constList.map((item) => ({
    id: item.constantId,
    title: item.desc,
    ...item.schema,
}));
export const getGlobalConstantSchema = (frontConstantList) => ({
    id: 'global',
    title: $t('k_afa73ddc'),
    type: 'object',
    readonly: true,
    'uc:collapsed': true,
    fields: getGlobalConstantFields(frontConstantList),
});
const getStaffInternalFields = () => (window.GLOBAL_INFO?.externalDeploy
    ? []
    : [
        { id: 'workDeptId', title: $t('k_e18fb93'), type: 'string', readonly: true, hidden: false },
        { id: 'workDeptIdString', title: $t('k_24790f65'), type: 'string', readonly: true, hidden: false },
        { id: 'workOrgIdString', title: $t('k_559c8dc6'), type: 'string', readonly: true, hidden: false },
        { id: 'positionName', title: $t('k_2297b92e'), type: 'string', readonly: true, hidden: false },
    ]);
const getSSOFields = () => ((SSO_DEPLOY && window.GLOBAL_INFO?.user_sso_frontendData) ? [
    {
        id: 'frontendData',
        title: $t('k_c9f2cb71'),
        type: 'object',
        readonly: true,
        description: $t('k_6c2db129'),
    },
] : []);
const getSwitchAccountMethods = () => (SSO_DEPLOY ? [
    {
        id: 'switchAccount',
        title: $t('k_ac776471'),
        description: $t('k_169c9883'),
        arguments: [
            { id: 'shallRedirect', type: 'boolean', description: $t('k_26d2fcd9') },
        ],
    },
] : []);
const baseContextDataSchema = {
    fields: [
        { id: 'mode', title: $t('k_2c16c9d2'), type: 'string', readonly: true },
        { id: 'env', title: $t('k_7e0c6a8b'), type: 'string', readonly: true },
        {
            id: 'user',
            title: $t('k_58f08327'),
            type: 'object',
            readonly: true,
            'uc:collapsed': true,
            fields: [
                { id: 'userId', title: $t('k_31212c91'), type: 'string', readonly: true },
                { id: 'name', title: $t('k_818c3f63'), type: 'string', readonly: true },
                { id: 'chnName', title: $t('k_c415ce1d'), type: 'string', readonly: true },
                { id: 'deptName', title: $t('k_bef5e764'), type: 'string', readonly: true },
                { id: 'deptNameString', title: $t('k_2dc51da8'), type: 'string', readonly: true },
                { id: 'deptIdString', title: $t('k_12d3d68'), type: 'string', readonly: true },
                { id: 'staffId', title: $t('k_31212c91'), type: 'string', readonly: true, hidden: true },
                { id: 'avatar', title: $t('k_58481ad5'), type: 'string',
                    readonly: true,
                    ui: { type: getAvailableComponent('public', COMP.avatar) } },
                ...getSSOFields(),
                { id: 'editorRole', title: $t('k_ffa8f8db'), type: 'string', readonly: true },
                { id: 'isAdmin', title: $t('k_a1e9870d'), type: 'boolean', readonly: true },
                { id: 'isLogin', title: $t('k_f16dd585'), type: 'boolean', readonly: true },
                ...getStaffInternalFields(),
                {
                    id: 'roles',
                    title: $t('k_e80971f6'),
                    type: 'array',
                    description: $t('k_98888d52'),
                    readonly: true,
                    items: {
                        type: 'object',
                        readonly: true,
                        fields: [
                            { id: 'role', title: $t('k_7c6fcd47'), type: 'string', readonly: true },
                            {
                                id: 'permissions',
                                title: $t('k_43927821'),
                                type: 'array',
                                readonly: true,
                                items: {
                                    type: 'object',
                                    fields: [
                                        { id: 'permission', title: $t('k_f0e7c62e'), type: 'string', readonly: true },
                                        { id: 'pageId', title: $t('k_8a603ebf'), type: 'string', readonly: true },
                                        { id: 'role', title: $t('k_e15c4cd6'), type: 'string', readonly: true },
                                    ],
                                },
                            },
                        ],
                    },
                },
                { id: 'orgBgId', title: 'Bg ID', type: 'string', readonly: true, hidden: true },
                { id: 'orgDepartmentId', title: $t('k_e18fb93'), type: 'string', readonly: true, hidden: true },
                { id: 'orgCenterId', title: $t('k_7de1ec17'), type: 'string', readonly: true, hidden: true },
                { id: 'orgGroupId', title: $t('k_258fab2a'), type: 'string', readonly: true, hidden: true },
            ],
            methods: [
                { id: 'logout', title: $t('k_60038845'), description: $t('k_b9843d8a') },
                ...getSwitchAccountMethods(),
            ],
        },
        getGlobalConstantSchema(window.w?.frontConstantList || []),
        {
            id: 'ai',
            title: 'AI',
            tsType: 'import(\'openai\').default',
        },
        {
            id: 'message',
            title: $t('k_d0722a3'),
            type: 'object',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            methods: [
                {
                    id: 'success',
                    title: $t('k_53324722'),
                    arguments: [{ id: 'content', type: 'string', description: $t('k_7b4c99db') }],
                },
                {
                    id: 'warning',
                    title: $t('k_67160703'),
                    arguments: [{ id: 'content', type: 'string', description: $t('k_7b4c99db') }],
                },
                {
                    id: 'error',
                    title: $t('k_912d2327'),
                    arguments: [{ id: 'content', type: 'string', description: $t('k_7b4c99db') }],
                },
                {
                    id: 'info',
                    title: $t('k_d0722a3'),
                    arguments: [{ id: 'content', type: 'string', description: $t('k_7b4c99db') }],
                },
                {
                    id: 'loading',
                    title: $t('k_276350'),
                    arguments: [{ id: 'content', type: 'string', description: $t('k_7b4c99db') }],
                },
            ],
        },
        {
            id: '$app',
            title: $t('k_62c862d'),
            type: 'any',
            readonly: true,
            'uc:bindable': false,
            tsType: '$__UICoreContext.AppLessCode',
        },
        {
            id: 'workflow',
            title: $t('k_3e0d1f1f'),
            type: 'object',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            fields: [
                {
                    id: 'relatedEntityList',
                    title: $t('k_f70c4830'),
                    type: 'array',
                    readonly: true,
                    items: {
                        type: 'object',
                        fields: [
                            { id: '_id', title: $t('k_52092425'), type: 'string', readonly: true },
                            { id: 'handlers', title: $t('k_5cadee77'), type: 'string', readonly: true },
                            { id: 'status', title: $t('k_dc2b439f'), type: 'string', readonly: true },
                            { id: 'statusName', title: $t('k_6cc900fc'), type: 'string', readonly: true },
                            { id: 'flowStatus', title: $t('k_655aac6a'), type: 'string', readonly: true },
                            { id: 'env', title: $t('k_740ff4b6'), type: 'string', readonly: true },
                            { id: 'objectId', title: $t('k_cb83ebb3'), type: 'string', readonly: true },
                            { id: 'flowId', title: $t('k_ee653527'), type: 'string', readonly: true },
                        ],
                    },
                },
            ],
            methods: [
                {
                    id: 'fetchEntityList',
                    title: $t('k_429d6b95'),
                    description: '',
                    arguments: [
                        { id: 'type', type: 'string', description: $t('k_aebfbded') },
                        { id: 'filter', type: 'string', description: $t('k_4811b215') },
                    ],
                },
                {
                    id: 'fetchWorkflowEntity',
                    title: $t('k_9b54903b'),
                    description: '',
                    arguments: [
                        { id: 'entityId', type: 'string', description: $t('k_52092425') },
                    ],
                },
                {
                    id: 'fetchEntityRecords',
                    title: $t('k_8bf6ae24'),
                    description: '',
                    arguments: [
                        { id: 'entityId', type: 'string', description: $t('k_52092425') },
                    ],
                },
                {
                    id: 'start',
                    title: $t('k_d3d1a668'),
                    description: '',
                    arguments: [
                        { id: 'workflowId', type: 'string', description: $t('k_5d7bf6b6') },
                        { id: 'data', type: 'object', description: $t('k_79f63bf') },
                    ],
                },
                {
                    id: 'transition',
                    title: $t('k_5f7ffe5a'),
                    description: '',
                    arguments: [
                        { id: 'entityId', type: 'string', description: $t('k_52092425') },
                        { id: 'data', type: 'object', description: $t('k_79f63bf') },
                    ],
                },
                {
                    id: 'restart',
                    title: $t('k_7b1edde4'),
                    description: '',
                    arguments: [
                        { id: 'entityId', type: 'string', description: $t('k_52092425') },
                        { id: 'payload', type: 'object', description: $t('k_77728eba') },
                    ],
                },
                {
                    id: 'abort',
                    title: $t('k_61dc85c1'),
                    description: '',
                    arguments: [
                        { id: 'entityId', type: 'string', description: $t('k_52092425') },
                    ],
                },
                {
                    id: 'isEntityProcessing',
                    title: $t('k_eb6d4b42'),
                    description: '',
                    arguments: [
                        { id: 'entity', type: 'object', description: $t('k_6734ea01') },
                    ],
                },
                {
                    id: 'canApproveEntity',
                    title: $t('k_4ae8c9aa'),
                    description: '',
                    arguments: [
                        { id: 'entity', type: 'object', description: $t('k_6734ea01') },
                    ],
                },
                {
                    id: 'assignEntityHandlers',
                    title: $t('k_49eaa2fd'),
                    description: '',
                    arguments: [
                        { id: 'entityId', type: 'string', description: $t('k_52092425') },
                        { id: 'handlers', type: 'string', description: $t('k_57f040e8') },
                    ],
                },
            ],
        },
        {
            id: 'projectInfo',
            title: $t('k_95610e57'),
            type: 'object',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            fields: [
                { id: 'id', title: $t('k_295efc94'), type: 'string', readonly: true },
                { id: 'name', title: $t('k_1cba5fce'), type: 'string', readonly: true },
                { id: 'logo', title: $t('k_e4a17591'), type: 'string', readonly: true, ui: { type: getAvailableComponent('public', COMP.image) } },
            ],
        },
        {
            id: 'mainPageInfo',
            title: $t('k_f895903c'),
            type: 'object',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            fields: getPageInfoFields(),
        },
        {
            id: 'pageList',
            title: $t('k_fb10ca0'),
            type: 'array',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            items: {
                type: 'object',
                fields: getPageInfoFields(),
            },
        },
        {
            id: 'sidebar',
            title: $t('k_aa07e056'),
            type: 'object',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            methods: [
                {
                    id: 'getCurrentGroup',
                    title: $t('k_586efc1c'),
                    arguments: [],
                },
                {
                    id: 'setCurrentGroup',
                    title: $t('k_7dec6c3c'),
                    arguments: [
                        { id: 'name', type: 'string', description: $t('k_afb4aa71') },
                    ],
                },
                {
                    id: 'setSelectedMenu',
                    title: $t('k_f48a30c7'),
                    arguments: [
                        { id: 'names', type: 'array', items: { type: 'string' }, description: $t('k_7e490a7e') },
                    ],
                },
                {
                    id: 'setCollapsed',
                    title: $t('k_132bad1'),
                    arguments: [
                        { id: 'collapsed', type: 'boolean', description: $t('k_4dbacdc8') },
                    ],
                },
                {
                    id: 'getSidebar',
                    title: $t('k_fa23b63b'),
                    arguments: [],
                },
                {
                    id: 'getSystemSidebar',
                    title: $t('k_bedeeb66'),
                    arguments: [],
                },
                {
                    id: 'setSidebar',
                    title: $t('k_916a77e7'),
                    arguments: [
                        { id: 'sidebar', type: 'object', description: $t('k_7e1290ed') },
                    ],
                },
                {
                    id: 'getCurrentSidebarNames',
                    title: $t('k_e797ccc3'),
                    arguments: [],
                    returnValue: { type: 'array', items: { type: 'string' } },
                },
            ],
        },
        { id: 'host', title: $t('k_4cf0f0d9'), type: 'string', readonly: true },
        { id: 'path', title: $t('k_d9d8a40a'), type: 'string', readonly: true },
        { id: 'query', title: $t('k_c00f7bc'), type: 'object', readonly: true },
        { id: 'params', title: $t('k_6da6881a'), type: 'object', readonly: true },
        getOrgSchema(),
        getFlowSchema(),
    ],
    methods: [
        // 确认框
        {
            id: 'confirm',
            title: $t('k_fae03fe7'),
            arguments: [
                {
                    id: 'option',
                    type: 'object',
                    description: $t('k_91f2451d'),
                    fields: [
                        { id: 'title', type: 'string', required: true, description: $t('k_203b66') },
                        { id: 'content', type: 'string', description: $t('k_114ddf76') },
                        { id: 'okText', type: 'string', description: $t('k_aeeca8a8') },
                        { id: 'cancelText', type: 'string', description: $t('k_fc288d97') },
                        { id: 'onOk', type: 'any', description: $t('k_bc6de6f2') },
                        { id: 'onCancel', type: 'any', description: $t('k_8acb7f72') },
                    ],
                },
            ],
        },
        {
            id: 'showGlobalModal',
            title: $t('k_26932b3d'),
            description: $t('k_1d08bdb2'),
            isAsync: true,
            arguments: [
                { id: 'id', type: 'string', required: true, description: $t('k_627f6522') },
                { id: 'data', type: 'object', description: $t('k_8f4d4ef7') },
                { id: 'opt', type: 'object', description: $t('k_62eba813') },
            ],
        },
        {
            id: 'hideGlobalModal',
            title: $t('k_d09de7b1'),
            description: $t('k_d03427fa'),
            arguments: [
                { id: 'id', type: 'string', description: $t('k_9ff6c1c7') },
                { id: 'data', type: 'object', description: $t('k_12b0797c') },
                { id: 'opt', type: 'object', description: $t('k_73d39db1') },
            ],
        },
        // 云函数
        {
            id: 'callFunc',
            title: $t('k_68075d8e'),
            isAsync: true,
            arguments: [
                { id: 'funcName', type: 'string', required: true, description: $t('k_d417a854') },
                { id: 'axiosOptions', type: 'object', description: $t('k_2548f21'), tsType: 'import("axios").AxiosRequestConfig' },
            ],
        },
        // 公共参数
        {
            id: 'getCommonQueryValue',
            title: $t('k_4f5746ba'),
            arguments: [
                { id: 'key', type: 'string', required: true, description: $t('k_5ffe70a4') },
            ],
        },
        {
            id: 'getCommonQueryOptions',
            title: $t('k_3aaf7073'),
            arguments: [
                { id: 'key', type: 'string', required: true, description: $t('k_5ffe70a4') },
            ],
        },
        {
            id: 'fetchCommonQueryOptions',
            title: $t('k_5a45336a'),
            isAsync: true,
            description: $t('k_8159f032'),
            arguments: [
                { id: 'key', type: 'string', required: true, description: $t('k_5ffe70a4') },
            ],
        },
        // 权限
        {
            id: 'hasPermission',
            title: $t('k_abaf65be'),
            description: $t('k_6e8b016d'),
            arguments: [
                { id: 'permission', type: 'string', required: true, description: $t('k_d3ee0d51') },
                // 可选 pageId 参数，不用直接补全，因而注释
                // { id: 'pageId', type: 'string', description: '可选，指定需要校验的页面 id' },
            ],
        },
        {
            id: 'isRoleMemberOf',
            title: $t('k_a0b1dbea'),
            description: $t('k_a51eb04f'),
            arguments: [
                { id: 'role', type: 'string', required: true, description: $t('k_67e74c27') },
            ],
        },
        // 路由
        {
            id: 'navigateTo',
            title: $t('k_c8131ca6'),
            description: $t('k_7e667622'),
            arguments: getNavigateArguments(),
        },
        {
            id: 'redirectTo',
            title: $t('k_c8131ca6'),
            description: $t('k_8937bba9'),
            arguments: getNavigateArguments(),
        },
        {
            id: 'openPage',
            title: $t('k_ecbcd43c'),
            description: $t('k_e60d9761'),
            arguments: getNavigateArguments(),
        },
        {
            id: 'getResolveHref',
            title: $t('k_db8bd10f'),
            arguments: getNavigateArguments(),
            returnValue: { type: 'string' },
        },
        // 滚动
        {
            id: 'scrollTo',
            title: $t('k_42c96189'),
            arguments: [
                {
                    id: 'option',
                    type: 'object',
                    description: $t('k_882e2f7e'),
                    fields: [
                        { id: 'top', type: 'number', default: 0 },
                        { id: 'behavior', type: 'string', enum: [{ value: 'instant' }, { value: 'smooth' }] },
                    ],
                },
            ],
        },
        // 上报
        {
            id: 'report',
            title: $t('k_8f6c9596'),
            arguments: [
                {
                    id: 'data',
                    type: 'object',
                    required: true,
                    description: $t('k_b79979ab'),
                    fields: [
                        { id: 'event', type: 'string', required: true, description: $t('k_6c5950ca') },
                        { id: 'eid', type: 'string', required: true, description: $t('k_fe3bdda2') },
                    ],
                },
            ],
        },
    ],
};
const commonContextDataSchema = {
    fields: [
        getUtilsSchema(),
        {
            id: 'atoms',
            title: $t('k_35c5ef3b'),
            type: 'object',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            methods: [
                {
                    id: 'alert',
                    title: $t('k_a7445e17'),
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            fields: [
                                { id: 'text', type: 'string', required: true, description: $t('k_7454f20') },
                                { id: 'icon', type: 'string', enum: [{ value: '' }, { value: 'info' }, { value: 'success' }, { value: 'error' }, { value: 'success' }] },
                            ],
                        },
                    ],
                },
                {
                    id: 'confirm',
                    title: $t('k_e0784cdf'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            fields: [
                                { id: 'text', type: 'string', required: true, description: $t('k_7454f20') },
                                { id: 'centered', type: 'boolean', description: $t('k_dc53da43') },
                                { id: 'icon', type: 'string', description: $t('k_5aec5cc9') },
                                { id: 'okText', type: 'string', description: $t('k_aeeca8a8') },
                                { id: 'cancelText', type: 'string', description: $t('k_fc288d97') },
                                { id: 'onOk', type: 'any', description: $t('k_d7c5ccb1') },
                                { id: 'onCancel', type: 'any', description: $t('k_c5bb25fa') },
                            ],
                        },
                    ],
                    returnValue: { type: 'boolean' },
                },
                {
                    id: 'prompt',
                    title: $t('k_2a72389b'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            fields: [
                                { id: 'message', type: 'string', required: true, description: $t('k_7454f20') },
                                { id: 'defaultValue', type: 'string', description: $t('k_569ec1f3') },
                                {
                                    id: 'inputType',
                                    type: 'string',
                                    description: $t('k_83e832aa'),
                                    enum: [{ value: 'input' }, { value: 'textarea' }, { value: 'password' }],
                                },
                                { id: 'allowEmpty', type: 'boolean', description: $t('k_d9335985') },
                            ],
                        },
                    ],
                },
                {
                    id: 'message',
                    title: $t('k_1b7a7f0'),
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            fields: [
                                { id: 'text', type: 'string', required: true, description: $t('k_7454f20') },
                                { id: 'type', type: 'string', enum: [{ value: 'success' }, { value: 'error' }, { value: 'info' }, { value: 'loading' }, { value: 'warn' }] },
                                { id: 'duration', type: 'number', description: $t('k_9a6f497a') },
                            ],
                        },
                    ],
                },
                {
                    id: 'notification',
                    title: $t('k_b6200d91'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            fields: [
                                { id: 'message', type: 'string', required: true, description: $t('k_7454f20') },
                                { id: 'description', type: 'string', required: true, description: $t('k_409f2431') },
                                { id: 'type', type: 'string', description: $t('k_c67dcb32'), enum: [{ value: 'open' }, { value: 'error' }, { value: 'success' }, { value: 'warn' }] },
                                { id: 'placement', type: 'string', description: $t('k_571ea30a'), enum: [{ value: 'topLeft' }, { value: 'topRight' }, { value: 'bottomLeft' }, { value: 'bottomRight' }] },
                                { id: 'duration', type: 'number', description: $t('k_9a6f497a') },
                            ],
                        },
                    ],
                },
                {
                    id: 'validateForm',
                    title: $t('k_d6a5c795'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            fields: [
                                { id: 'target', type: 'string', required: true, description: $t('k_fae573e4') },
                                { id: 'errorMessage', type: 'string', description: $t('k_bd571250') },
                            ],
                        },
                    ],
                    returnValue: { type: 'boolean' },
                },
                {
                    id: 'invokeLessCode',
                    title: $t('k_28f1d79c'),
                    isAsync: true,
                    arguments: [
                        { id: 'methodName', type: 'string', required: true, description: $t('k_4dfab82e') },
                    ],
                },
                {
                    id: 'clipboardWriteText',
                    title: $t('k_2b78c99b'),
                    isAsync: true,
                    arguments: [
                        { id: 'text', type: 'string', required: true, description: $t('k_6cbb034e') },
                        {
                            id: 'opt',
                            type: 'object',
                            fields: [
                                { id: 'successMessage', type: 'boolean', description: $t('k_5180256') },
                            ],
                        },
                    ],
                },
                {
                    id: 'invokeDataSourceMethod',
                    title: $t('k_237ad5c'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            fields: [
                                { id: 'id', type: 'string', required: true, description: $t('k_e7fe36bf') },
                                { id: 'method', type: 'string', required: true, description: $t('k_988d37c') },
                                { id: 'args', type: 'any', description: $t('k_cd70652e') },
                            ],
                        },
                    ],
                },
                {
                    id: 'dataSourceReInit',
                    title: $t('k_551045b7'),
                    isAsync: true,
                },
                {
                    id: 'uploadToCOS',
                    title: $t('k_2b9ae399'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            fields: [
                                { id: 'cosId', type: 'string', required: true, description: $t('k_f31a8864') },
                                { id: 'file', type: 'string', required: true, description: $t('k_7a884ba') },
                                { id: 'path', type: 'string', description: $t('k_245df27d') },
                                { id: 'envConfig', type: 'object', description: $t('k_24ab53ce') },
                                { id: 'headers', type: 'object', description: $t('k_1ce6b236') },
                            ],
                        },
                    ],
                    returnValue: { type: 'string', description: $t('k_3d26a736') },
                },
                {
                    id: 'exportExcel',
                    title: $t('k_13a8199d'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            fields: [
                                { id: 'data', type: 'array', required: true, description: $t('k_b84166fd') },
                                {
                                    id: 'fields',
                                    type: 'array',
                                    required: true,
                                    description: $t('k_9cd4de06'),
                                    items: {
                                        type: 'object',
                                        fields: [
                                            { id: 'dataKey', type: 'string', required: true, description: $t('k_3bf04fc0') },
                                            { id: 'colName', type: 'string', required: true, description: $t('k_9d7b7cda') },
                                        ],
                                    },
                                },
                                { id: 'fileName', type: 'string', required: true, description: $t('k_4b3c20e6') },
                                { id: 'sheetName', type: 'string', required: true, description: $t('k_366deca9') },
                                { id: 'format', type: 'string', description: $t('k_72e2a3ca'), enum: [{ value: 'xlsx' }, { value: 'xlsm' }] },
                            ],
                        },
                    ],
                },
                {
                    id: 'importExcel',
                    title: $t('k_ffb2cb19'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            required: true,
                            description: $t('k_bc85a2de'),
                            fields: [
                                { id: 'raw', type: 'boolean', description: $t('k_33f341a6') },
                                { id: 'cellDates', type: 'boolean', description: $t('k_d797a577') },
                                { id: 'codepage', type: 'string', description: $t('k_a07ade') },
                            ],
                        },
                    ],
                    returnValue: { type: 'object' },
                },
                {
                    id: 'getFileData',
                    title: $t('k_a58e76c4'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'multiple', type: 'boolean', description: $t('k_20e57fd2') },
                                { id: 'enableDirectory', type: 'boolean', description: $t('k_170853af') },
                                { id: 'codepage', type: 'string', description: $t('k_a07ade') },
                            ],
                        },
                    ],
                },
                {
                    id: 'openLink',
                    title: $t('k_c8cd5c3d'),
                    isAsync: true,
                    arguments: [
                        { id: 'link', type: 'string', required: true, description: $t('k_bd8fd6b6') },
                        { id: 'newWindow', type: 'boolean', description: $t('k_7813c8d2') },
                    ],
                },
                {
                    id: 'historyBack',
                    title: $t('k_83dee5ca'),
                },
                {
                    id: 'refreshPage',
                    title: $t('k_f74babfc'),
                },
                {
                    id: 'reloadRenderer',
                    title: $t('k_60faae12'),
                },
                {
                    id: 'pageScroll',
                    title: $t('k_95ef785e'),
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'position', type: 'string', required: true, description: $t('k_3e45c1a2'), enum: [{ value: 'top' }, { value: 'bottom' }, { value: 'y' }, { value: 'component' }] },
                                { id: 'y', type: 'number', description: $t('k_c489e648') },
                                { id: 'componentId', type: 'string', description: $t('k_53e5978f') },
                                { id: 'componentOffsetType', type: 'string', description: $t('k_3c4a03fc'), enum: [{ value: 'start' }, { value: 'end' }] },
                                { id: 'componentOffset', type: 'number', description: $t('k_e3c840a4') },
                                { id: 'behavior', type: 'string', description: $t('k_2ec227fd'), enum: [{ value: 'instant' }, { value: 'smooth' }] },
                            ],
                        },
                    ],
                },
                {
                    id: 'saveDraft',
                    title: $t('k_a02b9455'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'namespace', type: 'string', required: true, description: $t('k_72789283') },
                                { id: 'showConfirm', type: 'boolean', description: $t('k_9419baaa') },
                            ],
                        },
                    ],
                },
                {
                    id: 'restoreDraft',
                    title: $t('k_8a892778'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'namespace', type: 'string', required: true, description: $t('k_72789283') },
                                { id: 'showConfirm', type: 'boolean', description: $t('k_9419baaa') },
                                { id: 'remainDraftAfterCancel', type: 'boolean', description: $t('k_eca0065d') },
                            ],
                        },
                    ],
                },
                {
                    id: 'removeDraft',
                    title: $t('k_e1ac22e6'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'namespace', type: 'string', required: true, description: $t('k_72789283') },
                            ],
                        },
                    ],
                },
                {
                    id: 'email',
                    title: $t('k_7fd7aa57'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'to', type: 'string', required: true, description: $t('k_b2408937') },
                                { id: 'cc', type: 'string', description: $t('k_574c45bf') },
                                { id: 'bcc', type: 'string', description: $t('k_8b3d6bc9') },
                                { id: 'title', type: 'string', required: true, description: $t('k_25273424') },
                                { id: 'content', type: 'string', required: true, description: $t('k_2a5af184') },
                            ],
                        },
                    ],
                },
                {
                    id: 'wxworkRobot',
                    title: $t('k_74a14ee8'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'robotId', type: 'string', required: true, description: $t('k_4cdb7f0f') },
                                { id: 'content', type: 'string', required: true, description: $t('k_35433cae') },
                                { id: 'successMessage', type: 'boolean', required: true, description: $t('k_7864dd63') },
                            ],
                        },
                    ],
                },
                {
                    id: 'wxworkNotification',
                    title: $t('k_781e2f73'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'app', type: 'string', required: true, description: $t('k_295efc94') },
                                { id: 'receivers', type: 'string', required: true, description: $t('k_9dadd48') },
                                { id: 'type', type: 'string', required: true, description: $t('k_c67dcb32'), enum: [{ value: 'text' }, { value: 'textcard' }, { value: 'markdown' }] },
                                { id: 'content', type: 'string', description: $t('k_35433cae') },
                                { id: 'title', type: 'string', description: $t('k_3fa7cb03') },
                                { id: 'description', type: 'string', description: $t('k_64574092') },
                                { id: 'url', type: 'string', description: $t('k_c58ad71c') },
                                { id: 'buttonText', type: 'string', description: $t('k_5d3edd0d') },
                            ],
                        },
                    ],
                },
                {
                    id: 'openWxwork',
                    title: $t('k_c3e7de2c'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'type', type: 'string', required: true, enum: [{ value: 'username' }, { value: 'uin' }] },
                                { id: 'value', type: 'string', required: true },
                            ],
                        },
                    ],
                },
                {
                    id: 'sendGptMessage',
                    title: $t('k_81ce6af6'),
                    isAsync: true,
                    arguments: [
                        {
                            id: 'params',
                            type: 'object',
                            fields: [
                                { id: 'systemContent', type: 'string', required: true, description: $t('k_d8da8e3b') },
                                { id: 'message', type: 'string', required: true, description: $t('k_b771908f') },
                                { id: 'type', type: 'string', enum: [{ value: 'sync' }, { value: 'flow' }], description: $t('k_edbfb7bf') },
                                {
                                    id: 'flowSetting',
                                    type: 'object',
                                    fields: [
                                        { id: 'flowType', type: 'string', required: true, enum: [{ value: 'joinString' }, { value: 'callMethod' }] },
                                        { id: 'jsonString', type: 'string', description: $t('k_f149481e') },
                                        { id: 'callMethod', type: 'string', description: $t('k_e3d80c02') },
                                    ],
                                },
                                { id: 'model', type: 'string', description: $t('k_de254a4c') },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'mainRenderer',
            title: $t('k_73fd7845'),
            type: 'any',
            tsType: 'Vue',
            readonly: true,
            'uc:bindable': false,
        },
        {
            id: 'filter',
            title: $t('k_80c0aaf2'),
            type: 'object',
            readonly: true,
            methods: [
                {
                    id: 'toFilterArray',
                    title: $t('k_8e6b702a'),
                    description: '',
                    arguments: [
                        { id: 'filter', type: 'object', description: $t('k_8d7678d2') },
                        { id: 'options', type: 'object', description: $t('k_4efef7cc') },
                    ],
                },
                {
                    id: 'toFilterExpr',
                    title: $t('k_3c77f50b'),
                    description: '',
                    arguments: [
                        { id: 'filter', type: 'object', description: $t('k_8d7678d2') },
                        { id: 'type', type: 'string', description: $t('k_9ff5ee55') },
                        { id: 'options', type: 'object', description: $t('k_4efef7cc') },
                    ],
                },
                {
                    id: 'toNestedFilterExpr',
                    title: $t('k_fdf548a5'),
                    description: '',
                    arguments: [
                        { id: 'filter', type: 'object', description: $t('k_8d7678d2') },
                    ],
                },
            ],
        },
        {
            id: 'emitter',
            title: $t('k_92b24ea1'),
            type: 'object',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            fields: [
                {
                    id: 'EventName',
                    title: $t('k_c62f4b2e'),
                    type: 'object',
                    readonly: true,
                    'uc:bindable': false,
                    'uc:collapsed': true,
                    fields: [
                        { id: 'ROUTER_BEFORE_EACH', title: $t('k_bb2b4cb9'), type: 'string' },
                        { id: 'ROUTER_AFTER_EACH', title: $t('k_7a79173a'), type: 'string' },
                        { id: 'PAGE_BEFORE_DESTROY', title: $t('k_e3897292'), type: 'string' },
                        { id: 'PAGE_DESTROYED', title: $t('k_785f320d'), type: 'string' },
                        { id: 'PAGE_DATASOURCE_READY', title: $t('k_256b44be'), type: 'string' },
                        { id: 'PAGE_TITLE_CHANGE', title: $t('k_55a299cb'), type: 'string' },
                    ],
                },
            ],
            methods: [
                {
                    id: 'on',
                    title: $t('k_c0cd96a2'),
                    description: '',
                    arguments: [
                        { id: 'eventName', type: 'string', description: $t('k_e7a9a544'), required: true },
                        { id: 'fn', type: 'any', description: $t('k_16036785'), required: true },
                    ],
                },
                {
                    id: 'once',
                    title: $t('k_105baf88'),
                    description: '',
                    arguments: [
                        { id: 'eventName', type: 'string', description: $t('k_e7a9a544'), required: true },
                        { id: 'fn', type: 'any', description: $t('k_16036785'), required: true },
                    ],
                },
                {
                    id: 'off',
                    title: $t('k_15e5c942'),
                    description: '',
                    arguments: [
                        { id: 'eventName', type: 'string', description: $t('k_e7a9a544'), required: true },
                        { id: 'fn', type: 'any', description: $t('k_16036785') },
                    ],
                },
                {
                    id: 'emit',
                    title: $t('k_1ab8c1a5'),
                    description: '',
                    arguments: [
                        { id: 'eventName', type: 'string', description: $t('k_e7a9a544'), required: true },
                        { id: 'args', type: 'any', description: $t('k_d4a9d62c') },
                    ],
                },
            ],
        },
        {
            id: 'permission',
            title: $t('k_e372f2e4'),
            type: 'object',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            methods: [
                {
                    id: 'fetchRoleList',
                    title: $t('k_f9be4ad1'),
                    description: '',
                    arguments: [],
                },
                {
                    id: 'fetchRoleUserList',
                    title: $t('k_7f348e0d'),
                    description: '',
                    arguments: [
                        { id: 'bizId', type: 'string', description: $t('k_3d85e6a6'), default: '' },
                        { id: 'roleId', type: 'string', description: $t('k_cd0555c3'), default: '' },
                        { id: 'opt', type: 'object', description: $t('k_279c45f4'), default: { id: '', type: '' } },
                    ],
                },
                {
                    id: 'fetchRolePermissionList',
                    title: $t('k_db87fcc6'),
                    description: '',
                    arguments: [
                        { id: 'roleId', type: 'string', description: $t('k_7dacca34') },
                    ],
                },
                {
                    id: 'fetchRoleView',
                    title: $t('k_3202894a'),
                    description: '',
                    arguments: [
                        { id: 'roleId', type: 'string', description: $t('k_7dacca34') },
                    ],
                },
                {
                    id: 'fetchPermissionList',
                    title: $t('k_f0734e26'),
                    description: '',
                    arguments: [],
                },
                {
                    id: 'fetchBuiltinPermissionList',
                    title: $t('k_6e169561'),
                    description: '',
                    arguments: [],
                },
                {
                    id: 'fetchApiPermissionList',
                    title: $t('k_3530f0a0'),
                    description: '',
                    arguments: [],
                },
                {
                    id: 'createRole',
                    title: $t('k_a7ec5ea4'),
                    description: '',
                    arguments: [
                        {
                            id: 'data',
                            title: $t('k_90a32c54'),
                            type: 'object',
                            fields: [
                                { id: 'name', type: 'string', description: $t('k_a55a7ae0') },
                                { id: 'cname', type: 'string', description: $t('k_e8d8436e') },
                                { id: 'type', enum: [{ value: '' }, { value: 'biz' }], description: $t('k_223d6da') },
                            ],
                        },
                    ],
                },
                {
                    id: 'updateRole',
                    title: $t('k_96472c02'),
                    description: '',
                    arguments: [
                        {
                            id: 'data',
                            title: $t('k_90a32c54'),
                            description: $t('k_51139969'),
                            type: 'object',
                            fields: [
                                { id: 'name', type: 'string', description: $t('k_a55a7ae0') },
                                { id: 'cname', type: 'string', description: $t('k_e8d8436e') },
                                { id: 'type', enum: [{ value: '' }, { value: 'biz' }], description: $t('k_223d6da') },
                            ],
                        },
                    ],
                },
                {
                    id: 'deleteRole',
                    title: $t('k_2ca306cf'),
                    description: '',
                    arguments: [
                        { id: 'name', type: 'string', description: $t('k_a55a7ae0') },
                    ],
                },
                {
                    id: 'createPermission',
                    title: $t('k_705f6629'),
                    description: '',
                    arguments: [
                        {
                            id: 'data',
                            title: $t('k_d3f94caf'),
                            type: 'object',
                            fields: [
                                { id: 'name', type: 'string', description: $t('k_6bbee035') },
                                { id: 'cname', type: 'string', description: $t('k_30e50eed') },
                                { id: 'type', enum: [{ value: 'page' }, { value: 'api' }, { value: 'set' }], description: $t('k_9ff5ee55') },
                                { id: 'relatedPages', type: 'string', description: $t('k_3fb6a1a4') },
                            ],
                        },
                    ],
                },
                {
                    id: 'updatePermission',
                    title: $t('k_e170e4e'),
                    description: '',
                    arguments: [
                        {
                            id: 'data',
                            title: $t('k_d3f94caf'),
                            description: $t('k_d1582cd3'),
                            type: 'object',
                            fields: [
                                { id: 'name', type: 'string', description: $t('k_6bbee035') },
                                { id: 'cname', type: 'string', description: $t('k_30e50eed') },
                                { id: 'type', enum: [{ value: 'page' }, { value: 'api' }, { value: 'set' }], description: $t('k_9ff5ee55') },
                                { id: 'relatedPages', type: 'string', description: $t('k_3fb6a1a4') },
                            ],
                        },
                    ],
                },
                {
                    id: 'deletePermission',
                    title: $t('k_cc7543f3'),
                    description: '',
                    arguments: [
                        { id: 'name', type: 'string', description: $t('k_6bbee035') },
                    ],
                },
                {
                    id: 'createRoleUser',
                    title: $t('k_8301c6b1'),
                    description: '',
                    arguments: [
                        {
                            id: 'data',
                            title: $t('k_51fe1907'),
                            type: 'object',
                            fields: [
                                { id: 'role', type: 'string', description: $t('k_a55a7ae0') },
                                { id: 'userId', type: 'string', description: $t('k_9a883776') },
                                { id: 'userName', type: 'string', description: $t('k_453d502a') },
                                { id: 'userType', enum: [{ value: 'staff' }, { value: 'org' }], description: $t('k_9cfb53e1') },
                                { id: 'bizId', type: 'string', description: $t('k_926914a6') },
                                { id: 'expireAt', type: 'number', description: $t('k_3a9728ab') },
                            ],
                        },
                    ],
                },
                {
                    id: 'deleteRoleUser',
                    title: $t('k_13edd81f'),
                    description: '',
                    arguments: [
                        { id: 'id', type: 'string', description: $t('k_4ff0f22b') },
                    ],
                },
                {
                    id: 'createRolePermission',
                    title: $t('k_9409dc33'),
                    description: '',
                    arguments: [
                        {
                            id: 'data',
                            title: $t('k_85068e89'),
                            type: 'object',
                            fields: [
                                { id: 'role', type: 'string', description: $t('k_a55a7ae0') },
                                { id: 'permission', type: 'string', description: $t('k_6bbee035') },
                                { id: 'pageId', type: 'string', description: $t('k_e3adca26') },
                            ],
                        },
                    ],
                },
                {
                    id: 'deleteRolePermission',
                    title: $t('k_c964b725'),
                    description: '',
                    arguments: [
                        { id: 'id', type: 'string', description: $t('k_5644e476') },
                    ],
                },
                {
                    id: 'batchDeleteRolePermission',
                    title: $t('k_f5b083d5'),
                    description: '',
                    arguments: [
                        { id: 'ids', type: 'array', description: $t('k_15a3b5a3'), items: { type: 'string' } },
                    ],
                },
                {
                    id: 'createApiPermission',
                    title: $t('k_f664145f'),
                    description: '',
                    arguments: [
                        {
                            id: 'data',
                            title: $t('k_8de7d690'),
                            type: 'object',
                            fields: [
                                { id: 'name', type: 'string', description: $t('k_4b4a1b59') },
                                { id: 'permission', type: 'string', description: $t('k_6bbee035') },
                                { id: 'api', type: 'string', description: '' },
                                { id: 'method', type: 'string', description: '' },
                                { id: 'type', enum: [{ value: 'collection-cgi' }, { value: 'cloud-function' }], description: '' },
                            ],
                        },
                    ],
                },
                {
                    id: 'deleteApiPermission',
                    title: $t('k_53effe81'),
                    description: '',
                    arguments: [
                        { id: 'id', type: 'string', description: $t('k_c084da63') },
                    ],
                },
            ],
        },
        // 获取运行时公告
        {
            id: 'announcement',
            title: $t('k_3d1a6848'),
            type: 'object',
            readonly: true,
            methods: [
                {
                    id: 'getList',
                    title: $t('k_d532a596'),
                    description: '',
                    arguments: [
                        { id: 'filter', type: 'string', description: $t('k_8d7678d2') },
                    ],
                },
                {
                    id: 'refresh',
                    title: $t('k_5aa163b5'),
                    description: '',
                },
            ],
        },
        {
            id: 'watermark',
            title: $t('k_b0885b08'),
            type: 'object',
            readonly: true,
            methods: [
                {
                    id: 'show',
                    title: $t('k_dd0849e'),
                },
            ],
        },
        { id: 'isMiniProgram', title: $t('k_f6b95efe'), type: 'boolean', readonly: true },
        { id: 'isNarrowScreen', title: $t('k_c4486ea2'), type: 'boolean', readonly: true },
        { id: 'screen', title: $t('k_3bf07f73'), type: 'object', readonly: true, 'uc:bindable': false },
    ],
    methods: [
        {
            id: 'getAtoms',
            title: $t('k_102d9733'),
            arguments: [
                { id: 'renderer', type: 'any', required: true, description: $t('k_f5153f68') },
            ],
        },
        {
            id: 'getComponentById',
            title: $t('k_3f5a3000'),
            arguments: [
                { id: 'id', type: 'string', required: true, description: $t('k_9f7a55ce') },
            ],
            isAsync: true,
            returnValue: {
                type: 'any',
                tsType: 'Vue',
                readonly: true,
            },
        },
        {
            id: 'getRenderByCompInstance',
            title: $t('k_18ef36e8'),
            arguments: [
                { id: 'comp', type: 'any', tsType: 'Vue', required: true, description: $t('k_9c25ea2') },
            ],
        },
        {
            id: 'waitForRendererMounted',
            title: $t('k_30d87fe6'),
            description: $t('k_bf14b9ec'),
            isAsync: true,
            arguments: [],
        },
        {
            id: 'waitForDataSourceReady',
            title: $t('k_aea7f265'),
            description: $t('k_e66628b3'),
            isAsync: true,
            arguments: [],
        },
        // HTTP 请求
        {
            id: 'request',
            title: $t('k_4cf8f45c'),
            description: $t('k_e8548831'),
            arguments: [
                { id: 'url', type: 'string', required: true, description: $t('k_15de3a8f') },
                { id: 'option', type: 'object', description: $t('k_be5485b') },
            ],
        },
        // 引入模块
        {
            id: 'require',
            title: $t('k_8f03d179'),
            arguments: [
                { id: 'path', type: 'string', required: true, description: $t('k_bdeab364') },
            ],
        },
        // 数据源
        {
            id: 'requestCgi',
            title: $t('k_91575c1f'),
            isAsync: true,
            arguments: [
                { id: 'collectionId', type: 'string', required: true, description: $t('k_aca16052') },
                { id: 'cgiId', type: 'string', required: true, description: $t('k_8fdb1f9e') },
                {
                    id: 'params',
                    type: 'object',
                    description: $t('k_4aa05650'),
                    fields: [
                        {
                            id: 'query',
                            type: 'object',
                            description: $t('k_32ec29ed'),
                        },
                        {
                            id: 'body',
                            type: 'object',
                            description: $t('k_888dd51b'),
                        },
                        {
                            id: 'headers',
                            type: 'object',
                            description: $t('k_acd0529e'),
                        },
                        {
                            id: 'params',
                            type: 'object',
                            description: $t('k_5f79b68f'),
                        },
                    ],
                },
            ],
        },
    ],
};
const pageletContextDataSchema = {
    fields: [
        {
            id: 'renderer',
            title: $t('k_3c08d9be'),
            type: 'any',
            tsType: 'Vue',
            readonly: true,
            'uc:bindable': false,
        },
        {
            id: 'pageInfo',
            title: $t('k_da7bb8c1'),
            type: 'object',
            readonly: true,
            'uc:bindable': false,
            'uc:collapsed': true,
            fields: getPageInfoFields(),
        },
    ],
    methods: [
        // 弹窗
        {
            id: 'showModal',
            title: $t('k_cef231d3'),
            description: $t('k_d92652c9'),
            isAsync: true,
            arguments: [
                { id: 'id', type: 'string', required: true, description: $t('k_f0721f42') },
                { id: 'data', type: 'object', description: $t('k_2adf79b1') },
            ],
        },
        {
            id: 'hideModal',
            title: $t('k_de91ca00'),
            description: $t('k_94fd1ac5'),
            arguments: [
                { id: 'id', type: 'string', description: $t('k_a7550b3a') },
                { id: 'data', type: 'object', description: $t('k_ec14de23') },
            ],
        },
    ],
};
const aiPageContextDataSchema = {
    fields: [],
    methods: [],
};
function getFitDataSchema(dataSchema) {
    return Object.freeze(fitSchemaFieldUIType(dataSchema));
}
export function getCommonContextDataSchema() {
    return getFitDataSchema(cloneDeep({
        id: 'w',
        title: $t('k_f0b03782'),
        type: 'object',
        readonly: true,
        'uc:collapsed': true,
        'uc:bindable': false,
        fields: [
            ...(baseContextDataSchema?.fields ?? []),
            ...(commonContextDataSchema?.fields ?? []),
        ],
        methods: [
            ...(baseContextDataSchema?.methods ?? []),
            ...(commonContextDataSchema?.methods ?? []),
        ],
    }));
}
export function getPageletContextDataSchema() {
    return getFitDataSchema(cloneDeep({
        id: 'w',
        title: $t('k_f0b03782'),
        type: 'object',
        readonly: true,
        'uc:collapsed': true,
        'uc:bindable': false,
        fields: [
            ...(baseContextDataSchema?.fields ?? []),
            ...(commonContextDataSchema?.fields ?? []),
            ...(pageletContextDataSchema?.fields ?? []),
        ],
        methods: [
            ...(baseContextDataSchema?.methods ?? []),
            ...(commonContextDataSchema?.methods ?? []),
            ...(pageletContextDataSchema?.methods ?? []),
        ],
    }));
}
export function getAIPageContextDataSchema() {
    let baseFields = cloneDeep(baseContextDataSchema?.fields ?? []);
    const baseMethods = cloneDeep(baseContextDataSchema?.methods ?? []);
    // 移除 projectInfo, pageInfo, mainPageInfo, pageList 字段
    baseFields = baseFields.filter(field => !['projectInfo', 'pageInfo', 'mainPageInfo', 'pageList'].includes(field.id));
    // 移除 sidebar 除了 setCollapsed 方法外的其他方法
    const sidebar = baseFields.find(field => field.id === 'sidebar');
    if (sidebar?.methods?.length) {
        sidebar.methods = sidebar.methods.filter(method => ['setCollapsed'].includes(method.id));
    }
    const aiPageFields = cloneDeep(aiPageContextDataSchema?.fields ?? []);
    const aiPageMethods = cloneDeep(aiPageContextDataSchema?.methods ?? []);
    return getFitDataSchema(cloneDeep({
        id: 'wuji',
        title: $t('k_a25ae439'),
        type: 'object',
        readonly: true,
        fields: [
            ...baseFields,
            ...aiPageFields,
        ],
        methods: [
            ...baseMethods,
            ...aiPageMethods,
        ],
    }));
}
